import type { ReactNode } from 'react';
import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box, lighten, Typography, useTheme } from '@mui/material';
import { isFriday, isWeekend } from 'date-fns';

import type { CompDate } from '@/openapi';
import { nbsp } from '@/util/unicode';

// Width (in theme.spacing units) of a single compensation-report column.
// Every cell width and the card's min-width derive from this constant so the
// header row, day cells, per-row totals, and layout stay in agreement.
// Bump this value to make the report boxes wider. A "wide" cell (name column
// and the month/quarter/year totals) is always CellWidth * 2.
export const CellWidth = 4.5;

// Font size of the main text in the report cells (header day numbers, partner
// names, and the points value in each day cell). Kept in one place so the
// header and data rows always match. Nudge this to tighten the text
// horizontally within the boxes.
export const CellFontSize = '0.875rem';

export const colorMap = (theme: Theme) => [
  { shift: 'OB CALL', color: theme.palette.primary },
  { shift: 'OR CALL', color: theme.palette.secondary },
  { shift: 'CV CALL', color: theme.palette.orange },
  { shift: 'Float/ OR2', color: theme.palette.yellow },
];

export const mapShiftColor = (
  theme: Theme,
  shift: string,
  isWeekendShift: boolean
) => {
  const mapped = colorMap(theme).find((e) => e.shift === shift);
  if (mapped === undefined) {
    return undefined;
  }
  return isWeekendShift
    ? lighten(mapped.color.dark, 0.5)
    : lighten(mapped.color.dark, 0.8);
};

export const BasicCell: React.FC<{
  children?: ReactNode;
  width?: number;
  border?: boolean;
  background?: string;
}> = ({ children, width, border = true, background }) => {
  const theme = useTheme();
  return (
    <Box
      sx={[
        {
          display: 'inline-block',
          textAlign: 'center',
          backgroundColor: background,
          fontSize: CellFontSize,
          width: theme.spacing((width ?? 1) * CellWidth),
          height: theme.spacing(5),
          lineHeight: theme.spacing(5),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        border
          ? {
              border: '1.5px solid',
            }
          : {
              border: null,
            },
        border
          ? {
              borderColor: theme.palette.grey[600],
            }
          : {
              borderColor: null,
            },
      ]}>
      {children ?? nbsp}
    </Box>
  );
};

export const InvisibleCell: React.FC<{ width?: number }> = ({ width }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: theme.spacing((width ?? 1) * CellWidth),
        height: theme.spacing(5),
      }}>
      {nbsp}
    </Box>
  );
};

export const PointsCell: React.FC<{ day: CompDate }> = ({ day }) => {
  const theme = useTheme();
  const date = new Date(day.date);
  const weekendShading = isFriday(date) || isWeekend(date);
  const sx: SxProps = {
    backgroundColor: mapShiftColor(theme, day.shift, weekendShading),
  };

  const points =
    day.annotation === 'V' || day.annotation === 'NW' ? undefined : day.points;

  return <SplitCell points={points} annotation={day.annotation} sx={sx} />;
};
export const SplitCell: React.FC<{
  annotation?: string;
  points?: number;
  wide?: boolean;
  sx?: SxProps;
}> = ({ annotation, points, wide, sx }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'inline-block',
        textAlign: 'center',
        border: '1px solid',
        borderColor: theme.palette.grey[600],
        width: theme.spacing(wide ? CellWidth * 2 : CellWidth),
        height: theme.spacing(5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...sx,
      }}>
      <Box
        sx={{
          pt: 0.5,
          fontSize: CellFontSize,
        }}>
        {annotation && points === undefined
          ? nbsp
          : (points?.toLocaleString() ?? nbsp)}
      </Box>
      <Box
        sx={{
          height: theme.spacing(2),
        }}>
        <Typography
          sx={{
            fontSize: '0.6rem',
          }}>
          {annotation ?? nbsp}
        </Typography>
      </Box>
    </Box>
  );
};
export const TotalsCell: React.FC<{
  period?: string;
  annotation?: string;
  points?: number;
}> = ({ period, annotation, points }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        width: theme.spacing(20),
        height: theme.spacing(10),
      }}>
      <Box>
        <Typography
          sx={{
            fontSize: '0.8rem',
          }}>
          {period}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: '1.8rem',
          }}>
          {annotation && !points ? nbsp : (points?.toLocaleString() ?? nbsp)}
        </Typography>
      </Box>
      <Box
        sx={{
          height: theme.spacing(4),
        }}>
        <Typography
          sx={{
            fontSize: '1rem',
          }}>
          {annotation ?? nbsp}
        </Typography>
      </Box>
    </Box>
  );
};
